<template>
  <component
    :is="component"
    class="tapp-button"
    :aria-label="label || text"
    :class="[
      `tapp-button--${size}`,
      `tapp-button--${variant}`,
      { 'tapp-button--full-width': fullWidth },
      { 'tapp-button--full-width-mobile': fullWidthMobile },
      { 'tapp-button--pill': pill },
      { 'tapp-button--loading': loading },
    ]"
    :disabled="disabled || null"
  >
    <slot>
      {{ text }}
    </slot>
    <TappIcon v-if="loading" icon="loader" class="tapp-button__icon" :size="24" />
  </component>
</template>

<script lang="ts">
  export const tags = ['button', 'span', 'NuxtLink', 'a'] as const;
  export const sizes = ['default', 'small', 'large'] as const;
  export const variants = ['primary', 'secondary', 'tertiary', 'ghost', 'text'] as const;
</script>

<script lang="ts" setup>
  const {
    disabled = undefined,
    tag = tags[0],
    size = sizes[0],
    variant = variants[0],
    text = undefined,
    label = undefined,
  } = defineProps<{
    disabled?: HTMLButtonElement['disabled'];

    /**
     * The HTML tag (default is button)
     */
    tag?: (typeof tags)[number];

    /**
     * The size of the button
     */
    size?: (typeof sizes)[number];

    /**
     * The variant of the button
     */
    variant?: (typeof variants)[number];

    /**
     * Expands the button to 100% of available space
     */
    fullWidth?: boolean;

    /**
     * Expands the button to 100% of available space o mobile
     */
    fullWidthMobile?: boolean;

    /**
     * Disables the button and prepends the text with the TappSpinner component
     */
    loading?: boolean;

    /**
     * Gives a pill shape border radius to the button
     */
    pill?: boolean;

    /**
     * The text of the button
     */
    text?: string;

    /**
     * Can be used for a descriptive label for the button
     */
    label?: string;
  }>();

  const attrs = useAttrs();

  // when using :to as attribute we want <NuxtLink />
  const component = computed(() => {
    if (attrs.to) {
      return resolveComponent('NuxtLink');
    }
    return tag;
  });
</script>

<style lang="postcss" scoped>
  .tapp-button {
    --outline-offset: 3px;

    position: relative;

    overflow: hidden;
    display: flex;
    gap: var(--spacing-2);
    align-items: center;
    justify-content: center;

    width: fit-content;
    padding: calc(var(--spacing-2) - var(--button-ghost-border-width)) var(--spacing-4);
    border-style: solid;
    border-width: var(--button-ghost-border-width);
    border-radius: var(--button-border-radius);

    font: var(--typography-large-button-sm);
    font-family: var(--font-families-button);
    font-size: inherit;
    line-height: 24px; /* The line-height is the same as the potential loading icon in this component */
    text-align: center;
    text-decoration: none;
    text-overflow: ellipsis;
    text-transform: var(--text-transform-button-sm);
    letter-spacing: var(--text-spacing-button-sm);

    appearance: none;
    outline: 2px solid transparent;
    outline-offset: calc(var(--outline-offset) + var(--spacing-1));

    transition-timing-function: ease-in-out;
    transition-duration: 150ms;
    transition-property: background-color, border-color, transform, color, outline-offset;

    @media (--screen-md) {
      font: var(--typography-large-button-base);
      line-height: 24px; /* The line-height is the same as the potential loading icon in this component */
      text-transform: var(--text-transform-button-base);
      letter-spacing: var(--text-spacing-button-base);

      &.tapp-button--small {
        font: var(--typography-small-button-base);
        line-height: 24px; /* The line-height is the same as the potential loading icon in this component */
        text-transform: var(--text-transform-button-base);
      }
    }

    &:hover {
      cursor: pointer;
    }

    &[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:focus-visible {
      outline-width: 2px;
      outline-offset: var(--outline-offset);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .tapp-button--small {
    padding: calc(0.375rem - var(--button-ghost-border-width)) var(--spacing-4);
  }

  .tapp-button--large {
    padding: calc(var(--spacing-3) - var(--button-ghost-border-width)) var(--spacing-12);
  }

  .tapp-button--full-width {
    width: 100%;
    max-width: 100%;
  }

  .tapp-button--full-width-mobile {
    width: 100%;
    max-width: 100%;

    @media (--screen-lg) {
      width: fit-content;
      max-width: none;
    }
  }

  .tapp-button--pill {
    border-radius: var(--border-radius-pill);
  }

  .tapp-button--primary {
    border-color: var(--button-primary-default-background-color);
    color: var(--button-primary-default-text-color);
    background-color: var(--button-primary-default-background-color);

    &:not(:disabled):hover {
      border-color: var(--button-primary-hover-background-color);
      color: var(--button-primary-hover-text-color);
      background-color: var(--button-primary-hover-background-color);
    }

    &:focus-visible {
      outline-color: var(--button-primary-hover-background-color);
    }
  }

  .tapp-button--secondary {
    border-color: var(--button-secondary-default-background-color);
    color: var(--button-secondary-default-text-color);
    background-color: var(--button-secondary-default-background-color);

    &:not(:disabled):hover {
      border-color: var(--button-secondary-hover-background-color);
      color: var(--button-secondary-hover-text-color);
      background-color: var(--button-secondary-hover-background-color);
    }

    &:focus-visible {
      outline-color: var(--button-secondary-hover-background-color);
    }
  }

  .tapp-button--tertiary {
    border-color: var(--button-tertiary-default-background-color);
    color: var(--button-tertiary-default-text-color);
    background-color: var(--button-tertiary-default-background-color);

    &:not(:disabled):hover {
      border-color: var(--button-tertiary-hover-background-color);
      color: var(--button-tertiary-hover-text-color);
      background-color: var(--button-tertiary-hover-background-color);
    }

    &:focus-visible {
      outline-color: var(--button-tertiary-hover-background-color);
    }
  }

  .tapp-button--ghost {
    border-color: var(--button-ghost-default-border-color);
    border-width: var(--button-ghost-border-width);
    color: var(--button-ghost-default-text-color);
    background-color: transparent;

    &:not(:disabled):hover {
      border-color: var(--button-ghost-hover-border-color);
      color: var(--button-ghost-hover-text-color);
      background-color: transparent;
    }
  }

  .tapp-button--text {
    border: 0;
    color: var(--button-ghost-default-text-color);
    background-color: transparent;

    &:not(:disabled):hover {
      border-color: var(--button-ghost-hover-border-color);
      color: var(--button-ghost-hover-text-color);
      background-color: transparent;
    }
  }

  .tapp-button--loading {
    .tapp-button__icon {
      transform-origin: center;
      animation: spin 1s linear infinite;

      @keyframes spin {
        100% {
          transform: rotate(360deg);
        }
      }
    }
  }
</style>
